import React, { useState } from 'react'
import { t } from 'i18next'
import { isEmpty, isNil } from 'lodash'
import { type TabProps } from '../types'
import { Badge, Icon, IconSize, Tab } from '@awell-health/design-system'
import { DataPointValueType, ExtensionActivityOutputs } from '../../../../generated/types-orchestration'
import { ExpandableContent } from '../../ExpandableContent'
import { mapFieldTypeIcon } from './mapFieldTypeIcon'

export const ReceivedTab: React.FC<TabProps> = ({ activity, extensionActivityRecord }) => {
  const [selectedTab, setSelectedTab] = useState<'received' | 'saved'>('received')

  const dataPoints = extensionActivityRecord?.data_points

  const noDataPoints = isNil(dataPoints) || isEmpty(dataPoints)
  const allDataReceived = (activity.outputs as ExtensionActivityOutputs)?.extension_results ?? {}
  const hasFields = Object.keys(allDataReceived).length > 0

  const formatDataPointValue = (value: string, valueType: any) => {
    // right now we only need to format boolean values
    // but can add more formats here
    if (valueType.toLowerCase() === 'boolean') {
      return value === '0' ? 'false' : 'true'
    }
    return value
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="text-lg font-medium text-gray-900">Data received from {activity.indirect_object?.name}</div>
      <Tab
        fullWidth={false}
        items={[
          {
            id: 'received',
            label: `All ${hasFields ? `(${Object.keys(allDataReceived).length})` : ''}`,
            onClick: () => {
              setSelectedTab('received')
            }
          },
          {
            id: 'saved',
            label: `Saved ${dataPoints?.length ? `(${dataPoints.length})` : ''}`,
            onClick: () => {
              setSelectedTab('saved')
            }
          }
        ]}
        selected={selectedTab}
        size="sm"
        variant="default"
      />
      {selectedTab === 'received' && hasFields && (
        <div className="flex flex-col gap-2 w-full">
          <div className="text-sm text-slate-500 pb-2 pl-2">
            {t('activityfeed:extension_data_received', { name: activity.indirect_object?.name })}
          </div>
          {Object.entries(allDataReceived).map(([key, value]) => (
            <div key={key} className="rounded-xl border p-3">
              <div className="flex flex-col gap-2">
                <Badge size={null} variant="default">
                  {key}
                </Badge>
                <ExpandableContent content={value as string} />
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedTab === 'received' && !hasFields && (
        <div className="text-sm text-slate-500 pb-2 pl-2">
          {t('activityfeed:extension_no_data_received', { name: activity.indirect_object?.name })}
        </div>
      )}
      {selectedTab === 'saved' && noDataPoints && (
        <div className="text-sm text-slate-500 pb-2 pl-2">
          {t('activityfeed:extension_no_data_points', { name: activity.indirect_object?.name })}
        </div>
      )}
      {selectedTab === 'saved' && !noDataPoints && (
        <div className="flex flex-col gap-2 w-full">
          <div className="text-sm text-slate-500 pb-2 pl-2">
            {t('activityfeed:extension_saved_data_points', {
              count: dataPoints.length,
              total: Object.keys(allDataReceived).length
            })}
          </div>
          {dataPoints.map(({ label, value, valueType }) => (
            <div key={label} className="rounded-xl border p-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-900">Saved in</div>
                  <Badge size={null} variant="success">
                    <Icon
                      icon={mapFieldTypeIcon(valueType.toUpperCase() as DataPointValueType)}
                      size={IconSize.xxs}
                      className="mr-1"
                    />
                    {label}
                  </Badge>
                </div>
                <ExpandableContent content={formatDataPointValue(value, valueType)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
